import type { Config } from "./types";

// No `import "node:process"` here: this config is imported by client code,
// and Vite externalizes node builtins in the browser, which turns any
// `process.env` access into a runtime crash during hydration (dead client
// JS on every page). The local declaration also sidesteps the TS7 issue
// where ambient @types/node isn't picked up for a file resolved through
// `../../` from the ~19 consuming packages. On the server `process` exists;
// in the browser the guard yields an empty object, which is correct:
// secrets must never reach the client.
//
// Public values the browser needs are the exception: apps/web/vite.config.ts
// `define` bakes them in by replacing the literal `process.env.NAME`
// expression, so they are written out that way below (`env["NAME"]` is
// always empty in the browser). On the server they read the runtime env.
declare const process: {
	env: { AVATARS_BUCKET_NAME?: string } & Record<string, string | undefined>;
};
const env: Record<string, string | undefined> =
	typeof process === "undefined" ? {} : process.env;

export const config = {
	appName: "Hephon Commerce",
	appSlug: "hephoncommerce",
	appDomain: "commerce.hephon.ai",
	appDescription: "AI shopping agents for retailers",
	apiKeyPrefix: "hephoncommerce",
	// Rate Limiting
	rateLimit: {
		enabled: true,
		// Use Redis for distributed rate limiting (recommended for production)
		// Falls back to memory store if Redis URL is not available
		store: "redis" as const,
		redis: {
			url: env["REDIS_URL"] ?? "",
		},
		limits: {
			api: { window: 60000, max: 100 }, // 100 requests per minute
			auth: { window: 300000, max: 10 }, // 10 attempts per 5 minutes
			upload: { window: 3600000, max: 50 }, // 50 uploads per hour
			// 3 signups per 24h per IP. Pairs with Cloudflare Turnstile to curb
			// scripted multi-account farming. Fails open on Redis outage.
			"signup-ip": { window: 24 * 60 * 60 * 1000, max: 3 },
		},
	},
	// Background Jobs
	jobs: {
		enabled: env["JOBS_ENABLED"] !== "false",
		redis: {
			url: env["REDIS_URL"] ?? "redis://localhost:6379",
		},
		workers: {
			email: { concurrency: 5 },
			scheduled: { concurrency: 3 },
		},
	},
	// Security
	security: {
		failedLogin: {
			enabled: true,
			baseDelayMs: 1000, // 1 second base delay
			maxDelayMs: 30000, // 30 seconds max delay
			delayMultiplier: 2, // Exponential backoff multiplier
		},
		accountLockout: {
			enabled: true,
			maxFailedAttempts: 5, // Lock after 5 failed attempts
			lockoutDurationMinutes: 30, // Lock for 30 minutes
			notifyOnLockout: true, // Send email when account is locked
		},
		deviceTracking: {
			enabled: true,
			notifyNewDevice: true, // Send email on new device login
			maxKnownDevices: 10, // Keep track of last 10 devices
		},
	},
	// Internationalization
	i18n: {
		// Whether internationalization should be enabled (if disabled, you still need to define the locale you want to use below and set it as the default locale)
		enabled: true,
		// Define all locales here that should be available in the app
		// You need to define a label that is shown in the language selector and a currency that should be used for pricing with this locale
		locales: {
			en: {
				currency: "USD",
				label: "English",
			},
			de: {
				currency: "USD",
				label: "Deutsch",
			},
		},
		// The default locale is used if no locale is provided
		defaultLocale: "en",
		// The default currency is used for pricing if no currency is provided
		defaultCurrency: "USD",
		// The name of the cookie that is used to determine the locale
		localeCookieName: "LOCALE",
	},
	// Organizations
	organizations: {
		// Whether organizations are enabled in general
		enable: true,
		// Whether billing for organizations should be enabled (below you can enable it for users instead)
		enableBilling: false,
		// Whether the organization should be hidden from the user (use this for multi-tenant applications)
		hideOrganization: false,
		// Should users be able to create new organizations? Otherwise only admin users can create them
		enableUsersToCreateOrganizations: false,
		// Whether users should be required to be in an organization. This will redirect users to the organization page after sign in
		requireOrganization: true,
		// Define forbidden organization slugs. Make sure to add all paths that you define as a route after /app/... to avoid routing issues
		forbiddenOrganizationSlugs: [
			"new-organization",
			"admin",
			"settings",
			"organization-invitation",
			"platform",
			// Reserved for the integrations platform (hosts, paths and partner routes).
			"hephon",
			"api",
			"partner",
			"c",
			"app",
		],
	},
	// Users
	users: {
		// Whether billing should be enabled for users (above you can enable it for organizations instead)
		enableBilling: false,
		// Invite-only product: people join an existing retailer from an
		// invitation (name and password are collected at signup), so there is
		// no onboarding flow. The starter's onboarding module was removed.
		enableOnboarding: false,
	},
	// Authentication
	auth: {
		// Whether users should be able to create accounts (otherwise users can only be by admins)
		enableSignup: false,
		// Whether users should be able to sign in with a magic link
		enableMagicLink: true,
		// Whether users should be able to sign in with a social provider
		enableSocialLogin: true,
		// Whether users should be able to sign in with a passkey
		enablePasskeys: true,
		// Whether users should be able to sign in with a password
		enablePasswordLogin: true,
		// Whether users should be activate two factor authentication
		enableTwoFactor: true,
		// Whether Cloudflare Turnstile human-verification is enforced on signup.
		// The server check (packages/auth/lib/turnstile.ts) reads ENABLE_TURNSTILE
		// directly; this flag exposes the same toggle for the frontend widget.
		enableTurnstile: env["ENABLE_TURNSTILE"] === "true",
		// where users should be redirected after the sign in
		redirectAfterSignIn: "/app",
		// where users should be redirected after logout
		redirectAfterLogout: "/login",
		// how long a session should be valid
		sessionCookieMaxAge: 60 * 60 * 24 * 30,
	},
	// Mails
	mails: {
		// the from address for mails (configurable via env for dev/prod domains)
		from: env["MAIL_FROM"] || "noreply@mail.commerce.hephon.ai",
	},
	// Frontend
	ui: {
		// the themes that should be available in the app
		enabledThemes: ["light", "dark"],
		// the default theme
		defaultTheme: "light",
		// the saas part of the application
		saas: {
			// whether the saas part should be enabled (otherwise all routes will be redirect to the marketing page)
			enabled: true,
		},
		// the marketing part of the application
		marketing: {
			// whether the marketing features should be enabled (otherwise all routes will be redirect to the saas part)
			enabled: false,
		},
	},
	// Storage
	storage: {
		// define the name of the buckets for the different types of files
		bucketNames: {
			// Written out for Vite's `define`: see the note at the top.
			avatars: process.env.AVATARS_BUCKET_NAME || "hephoncommerce-dev",
		},
	},
	contactForm: {
		// whether the contact form should be enabled
		enabled: true,
		// the email to which the contact form messages should be sent
		to: "hello@hephon.ai",
		// the subject of the email
		subject: "Contact form message",
	},
	// Payments
	payments: {
		gracePeriodDays: 7,
		plans: {
			free: {
				isFree: true,
				title: "Free",
				description: "Get started for free",
				features: ["1 team member"],
				limits: {
					maxMembers: 1,
					teamEnabled: false,
					aiChatbotEnabled: false,
					integrationsEnabled: false,
				},
			},
			pro: {
				recommended: true,
				title: "Pro",
				description: "For growing teams",
				features: [
					"Up to 10 team members",
					"AI-powered insights",
					"Integrations",
					"Team collaboration",
				],
				limits: {
					maxMembers: 10,
					teamEnabled: true,
					aiChatbotEnabled: true,
					integrationsEnabled: true,
				},
				prices: [
					{
						type: "recurring",
						productId: env["VITE_PRICE_ID_PRO_MONTHLY"] as string,
						interval: "month",
						amount: 5,
						currency: "USD",
						seatBased: true,
						trialPeriodDays: 7,
					},
					{
						type: "recurring",
						productId: env["VITE_PRICE_ID_PRO_YEARLY"] as string,
						interval: "year",
						amount: 48,
						currency: "USD",
						seatBased: true,
						trialPeriodDays: 7,
					},
				],
			},
			legacy: {
				hidden: true,
				title: "Legacy",
				description: "Grandfathered plan for early adopters",
				features: ["All Pro features included"],
				limits: {
					maxMembers: 10,
					teamEnabled: true,
					aiChatbotEnabled: true,
					integrationsEnabled: true,
				},
				prices: [
					{
						type: "recurring" as const,
						productId: "legacy-plan",
						interval: "month" as const,
						amount: 0,
						currency: "USD",
					},
				],
			},
			enterprise: {
				isEnterprise: true,
				title: "Enterprise",
				description: "For large organizations with custom needs",
				features: [
					"Everything in Pro",
					"Unlimited team members",
					"Dedicated support",
					"Custom hosting & SSO",
					"SLA & priority onboarding",
				],
				limits: {
					maxMembers: -1,
					teamEnabled: true,
					aiChatbotEnabled: true,
					integrationsEnabled: true,
				},
			},
		},
	},
} as const satisfies Config;

export type {
	Config,
	JobsConfig,
	OneTimePlanPrice,
	PlanConfig,
	PlanLimits,
	PlanPrice,
	PlanPriceBase,
	RecurringPlanPrice,
	SecurityConfig,
} from "./types";
